import { deadlined, logged, retryable } from '@shared/decorators';
import { Asset, type AssetRepository, CountByState } from '@telespot/domain';
import { ParseBaseRepository } from '../../parse-base.repository';
import { AssetTopology, ParseAssetMapper } from './parse-asset.mapper';
import { SampleQueryBuilder } from './parse-sample-query.builder';
import { SampleAssetTopology } from './parse-sample-asset.mapper';
import { ObjectTopology } from '../../parse.topology';

export class ParseAssetRepository extends ParseBaseRepository implements AssetRepository {
  private readonly assetMapper = new ParseAssetMapper(this.parse);

  @retryable()
  @deadlined()
  @logged()
  public async getById(id: string): Promise<Asset | undefined> {
    try {
      const parseAsset = await new this.parse.Query(AssetTopology.TABLE).get(id, this.options);
      return this.assetMapper.toDomain(parseAsset);
    } catch (error: unknown) {
      if (error instanceof this.parse.Error && error.code === this.parse.Error.OBJECT_NOT_FOUND) {
        return undefined;
      }
      throw error;
    }
  }

  @logged()
  @deadlined()
  public async save(asset: Asset): Promise<string> {
    const parseAsset = this.assetMapper.fromDomain(asset);

    const { id } = await parseAsset.save(null, this.options);

    return id;
  }

  @logged()
  @retryable()
  @deadlined()
  public async findAssetWithFilename(assetFile: string): Promise<Asset | undefined> {
    const parseAsset = await new this.parse.Query(AssetTopology.TABLE)
      .equalTo(AssetTopology.ASSET_FILE, assetFile)
      .first(this.options);

    return parseAsset ? this.assetMapper.toDomain(parseAsset) : undefined;
  }

  @retryable()
  @deadlined()
  @logged()
  public async countByState(query: CountByState): Promise<number> {
    const { level, id, statusType, statusValue } = query;

    const statusKey = `${statusType}.state`;

    const sampleQuery = new SampleQueryBuilder(this.parse).withIds([id]).withLevel(level).build();

    const sampleAssetQuery = new this.parse.Query(SampleAssetTopology.TABLE)
      .matchesQuery(SampleAssetTopology.SAMPLE, sampleQuery)
      .exists(SampleAssetTopology.ASSET);

    const assetQuery = new this.parse.Query(AssetTopology.TABLE).matchesKeyInQuery(
      ObjectTopology.ID,
      `${SampleAssetTopology.ASSET}.${ObjectTopology.ID}`,
      sampleAssetQuery
    );

    return assetQuery.equalTo(statusKey, statusValue).count(this.options);
  }

  @retryable()
  @deadlined()
  @logged()
  public async findByUuids(...uuids: string[]): Promise<Asset[]> {
    if (!uuids.length) return [];

    const parseAssets = await new this.parse.Query(AssetTopology.TABLE)
      .containedIn(AssetTopology.UUID, uuids)
      .find(this.options);

    return parseAssets.map((a) => this.assetMapper.toDomain(a));
  }

  @retryable()
  @deadlined()
  @logged()
  public async findByIds(...ids: string[]): Promise<Asset[]> {
    const parseAssets = await new this.parse.Query(AssetTopology.TABLE)
      .containedIn(ObjectTopology.ID, ids)
      .find(this.options);

    return parseAssets.map((a) => this.assetMapper.toDomain(a));
  }

  @logged()
  @retryable()
  @deadlined()
  public async findAssetWithUuid(uuid: string): Promise<Asset | undefined> {
    const parseAsset = await new this.parse.Query(AssetTopology.TABLE)
      .equalTo(AssetTopology.UUID, uuid)
      .first(this.options);

    return parseAsset ? this.assetMapper.toDomain(parseAsset) : undefined;
  }
}
